import { storageService } from "./storageService";
import { FilterSection } from "../models/enums/filterSection";

const FILTER_STORAGE_PREFIX = "tm_filters";

class FilterStorageService {
    private getUserKeyPart(): string {
        try {
            const user = storageService.getUserData() as { id?: number } | null;
            if (user && user.id != null) {
                return `user_${user.id}`;
            }
        } catch {
            // ignore errors and fallback to anonymous
        }
        return "anonymous";
    }

    private getStorageKey(section: FilterSection): string {
        const userPart = this.getUserKeyPart();
        return `${FILTER_STORAGE_PREFIX}:${userPart}:${section}`;
    }

    /**
     * Loads raw value from sessionStorage for a given section.
     * Survives F5 within the same tab; empty for a new tab.
     */
    loadRaw<T>(section: FilterSection): T | null {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return null;
        }

        try {
            const key = this.getStorageKey(section);
            const raw = window.sessionStorage.getItem(key);
            if (!raw) {
                return null;
            }
            return JSON.parse(raw) as T;
        } catch {
            return null;
        }
    }

    /**
     * Saves raw value to sessionStorage for a given section.
     */
    saveRaw<T>(section: FilterSection, value: T | null): void {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return;
        }

        const key = this.getStorageKey(section);
        try {
            if (value === null) {
                window.sessionStorage.removeItem(key);
            } else {
                window.sessionStorage.setItem(key, JSON.stringify(value));
            }
        } catch {
            // ignore quota / serialization errors
        }
    }

    /**
     * Loads filter and merges it with provided defaultFilter.
     * Unknown / removed fields will stay at default values.
     */
    loadFilter<T extends object>(section: FilterSection, defaultFilter: T): T {
        const saved = this.loadRaw<Partial<T>>(section);
        if (!saved) {
            return defaultFilter;
        }
        return {
            ...defaultFilter,
            ...saved
        };
    }

    /**
     * Saves filter for a given section.
     */
    saveFilter<T>(section: FilterSection, filter: T): void {
        this.saveRaw(section, filter);
    }

    /**
     * Clears all stored filters for the current tab/session.
     * Only keys with FILTER_STORAGE_PREFIX are affected.
     */
    clearAll(): void {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return;
        }

        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < window.sessionStorage.length; i++) {
                const key = window.sessionStorage.key(i);
                if (key && key.startsWith(FILTER_STORAGE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(k => window.sessionStorage.removeItem(k));
        } catch {
            // ignore
        }
    }
}

export const filterStorageService = new FilterStorageService();
